import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  useContext,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  CalendarViewEnum,
  CalenderRefetchRefContext,
  isValidCalendarView,
  monthTranslation,
  AgendaSelectedUserContext,
  CalendarUpdateDateContext,
} from "./CalendarHelper";
import { useLocation } from "react-router-dom";
import classes from "./Calendar.module.css";
import classnames from "classnames";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import CalendarMonthView from "./CalendarViews/CalendarMonthView/CalendarMonthView";
import CalendarWeekView, {
  AgendaWeekView,
} from "./CalendarViews/CalendarWeekView/CalendarWeekView";
import Button from "../Button/Button";
import CalendarDailyView, {
  AgendaDailyView,
} from "./CalendarViews/CalendarDailyView/CalendarDailyView";
import { ServerAwareContext } from "../CGrid/ServerGrid";
import {
  faArrowsRotate,
  faCalendarAlt,
  faCalendarDay,
  faCalendarWeek,
} from "@fortawesome/pro-light-svg-icons";
import querystring from "query-string";
import { FilterColumnColorProvider } from "../FilterList/AdvancedFilter/AdvancedFilterColors/AdvancedFilterColors";
import {
  useAgendaPreferences,
  AgendaPreferenceContextr,
  AgendaCurrentViewChangesContext,
} from "../AgendaPreferences/AgendaPreferences";
import ArDatePicker from "../DatePicker/ArDatePicker";
import { AgendaUserDropdown } from "../../Containers/AdvancedMultiInputs";
import { useCurrentAccount } from "../../Contexts/UserContext";
import { isDeveloper } from "../../Helpers/ModulesHelper";
import UserAlterEgoButton from "../UserAlterEgoButton/UserAlterEgoButton";
import {
  removeAlterEgoDontChangeURL,
  getAlterEgo,
} from "../../Helpers/AuthHelper";

const CalendarContent = ({ type, selectedDate, setSelectedDate, settings }) => {
  const { all } = settings;

  switch (type) {
    case CalendarViewEnum.Month:
      return (
        <CalendarMonthView
          settings={settings}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
      );

    case CalendarViewEnum.Week:
      if (all)
        return (
          <AgendaWeekView
            settings={settings}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        );
      else
        return (
          <CalendarWeekView
            settings={settings}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        );

    case CalendarViewEnum.MultipleDay:
      if (all)
        return (
          <AgendaWeekView
            settings={settings}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            multipleDay
          />
        );
      else
        return (
          <CalendarWeekView
            settings={settings}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            multipleDay
          />
        );

    case CalendarViewEnum.WorkWeek:
      if (all)
        return (
          <AgendaWeekView
            settings={settings}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            workWeek
          />
        );
      else
        return (
          <CalendarWeekView
            workWeek
            settings={settings}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        );

    case CalendarViewEnum.Day:
      if (all)
        return (
          <AgendaDailyView
            settings={settings}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        );
      else
        return (
          <CalendarDailyView
            settings={settings}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        );

    default:
      if (all)
        return (
          <AgendaWeekView
            settings={settings}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        );
      else
        return (
          <CalendarWeekView
            settings={settings}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        );
  }
};

const ViewTypeSelectorItem = ({
  viewType,
  isSelected,
  textId,
  setViewType,
  icon,
}) => {
  const handleCurrentViewChanges = useContext(AgendaCurrentViewChangesContext);

  const handleClick = useCallback(() => {
    if (typeof handleCurrentViewChanges === "function") {
      handleCurrentViewChanges({
        calendarView: viewType,
      });
    } else setViewType(viewType);
  }, [handleCurrentViewChanges, setViewType, viewType]);

  return (
    <div
      onClick={handleClick}
      className={classnames(
        "p-2 fs-14 d-flex align-items-center",
        classes.viewSelectorButton,
        {
          [classes.selected]: isSelected,
        },
      )}
    >
      <FontAwesomeIcon icon={icon} className="mr-2" />
      <FormattedMessage id={textId} />
    </div>
  );
};
const ViewTypeSelector = ({ selectedView, setViewType, hideMonthly }) => {
  const agendaPreferences = useAgendaPreferences();

  const location = useLocation();

  const isOnAgenda = useMemo(() => {
    return (
      location.pathname.includes("calendar") ||
      location.pathname.includes("timemanager")
    );
  }, [location.pathname]);

  return (
    <div className={classnames("d-flex ml-2", classes.viewSelector)}>
      <ViewTypeSelectorItem
        icon={faCalendarDay}
        isSelected={selectedView === CalendarViewEnum.Day}
        setViewType={setViewType}
        viewType={CalendarViewEnum.Day}
        textId={"DAY"}
      />

      {isOnAgenda ? (
        <ViewTypeSelectorItem
          icon={faCalendarDay}
          isSelected={selectedView === CalendarViewEnum.MultipleDay}
          setViewType={setViewType}
          viewType={CalendarViewEnum.MultipleDay}
          textId={"7DAYS"}
        />
      ) : (
        <></>
      )}

      <ViewTypeSelectorItem
        icon={faCalendarWeek}
        isSelected={selectedView === CalendarViewEnum.Week}
        setViewType={setViewType}
        viewType={CalendarViewEnum.Week}
        textId={"WEEK"}
      />
      {agendaPreferences ? (
        <ViewTypeSelectorItem
          icon={faCalendarWeek}
          isSelected={selectedView === CalendarViewEnum.WorkWeek}
          setViewType={setViewType}
          viewType={CalendarViewEnum.WorkWeek}
          textId={"WORK_WEEK"}
        />
      ) : (
        <></>
      )}
      {!hideMonthly ? (
        <ViewTypeSelectorItem
          icon={faCalendarAlt}
          isSelected={selectedView === CalendarViewEnum.Month}
          setViewType={setViewType}
          viewType={CalendarViewEnum.Month}
          textId={"MONTHLY"}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

const CalendarHeader = ({
  selectedDate,
  setSelectedDate,
  viewType,
  selectedUser,
  setSelectedUser,
  setViewType,
  hideMonthly,
  disableViewSwitcher = false,
  resetDate,
  refetch,
}) => {
  const { year } = useMemo(() => {
    const month = selectedDate.getMonth();
    const year = selectedDate.getFullYear();
    return { month, year };
  }, [selectedDate]);

  // throttle clicks to prevent spam clicking
  const clickCooldownRef = useRef(false);
  const cooldownTimeoutRef = useRef(null);

  React.useEffect(() => {
    return () => {
      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
        cooldownTimeoutRef.current = null;
      }
    };
  }, []);

  const startCooldown = () => {
    if (clickCooldownRef.current) return false;
    clickCooldownRef.current = true;
    cooldownTimeoutRef.current = setTimeout(() => {
      clickCooldownRef.current = false;
      cooldownTimeoutRef.current = null;
    }, 600);
    return true;
  };

  const handleBack = useCallback(() => {
    if (!startCooldown()) return;

    let newD;
    const date = selectedDate.getDate();

    const month = selectedDate.getMonth();
    switch (viewType) {
      case CalendarViewEnum.Month:
        newD = new Date(year, month - 1, 1);
        break;

      case CalendarViewEnum.Week:
      case CalendarViewEnum.WorkWeek:
        newD = new Date(year, month, date - 7);
        break;

      case CalendarViewEnum.Day:
      case CalendarViewEnum.MultipleDay:
        newD = new Date(year, month, date - 1);
        break;
      default:
        break;
    }
    setSelectedDate(newD);
  }, [selectedDate, viewType, year, setSelectedDate]);

  const handleForward = useCallback(() => {
    if (!startCooldown()) return;

    let newD;

    const month = selectedDate.getMonth();
    const date = selectedDate.getDate();

    switch (viewType) {
      case CalendarViewEnum.Month:
        newD = new Date(year, month + 1, 1);
        break;

      case CalendarViewEnum.Week:
      case CalendarViewEnum.WorkWeek:
        newD = new Date(year, month, date + 7);
        break;

      case CalendarViewEnum.Day:
      case CalendarViewEnum.MultipleDay:
        newD = new Date(year, month, date + 1);
        break;
      default:
        break;
    }
    setSelectedDate(newD);
  }, [selectedDate, viewType, year, setSelectedDate]);

  const location = useLocation();

  const thisAccount = useCurrentAccount();

  const developer = isDeveloper(thisAccount);

  const isInAlterEgo = getAlterEgo();

  const isOnAgenda = useMemo(() => {
    return (
      location.pathname.includes("calendar") ||
      location.pathname.includes("timemanager")
    );
  }, [location.pathname]);

  return (
    <div
      // style={{ height: 50 }}
      style={{
        position: "sticky",
        top: "0",
        zIndex: "4",
        background: "white",
      }}
      className="pb-2 d-flex align-items-center justify-content-between text-black"
    >
      <div className="d-flex align-items-center">
        <div
          onClick={handleBack}
          className={classnames("mr-1", classes.dateArrow)}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </div>
        <div onClick={handleForward} className={classes.dateArrow}>
          <FontAwesomeIcon icon={faChevronRight} />
        </div>
        <div className="ml-2 mr-2 fs-22">
          <CalendarInput
            setSelectedDate={setSelectedDate}
            selectedDate={selectedDate}
          />
        </div>
      </div>
      <div className="d-flex align-items-center ">
        {isOnAgenda ? (
          <div className="d-flex items-center fixed40height">
            {isInAlterEgo ? (
              <Button
                onClick={removeAlterEgoDontChangeURL}
                type="button"
                vType="outline-danger"
                className="mr-3"
              >
                Sair do Alter Ego
              </Button>
            ) : (
              <></>
            )}
            {developer && selectedUser !== thisAccount.Id ? (
              <UserAlterEgoButton
                className={classnames(
                  "fixed40height mr-3",
                  classes["fixedWidth40"],
                )}
                canEdit
                noUrlChanges
                userId={selectedUser}
              />
            ) : (
              <></>
            )}
            <div className={classnames("mr-2 fixed40height", classes["w-200"])}>
              <AgendaUserDropdown
                unremovable
                className="m-0 p-0 fixed40height"
                allAccounts={true}
                value={selectedUser}
                hideOnEmpty
                type={1}
                onChange={(v) => setSelectedUser(v)}
              />
            </div>
          </div>
        ) : (
          <></>
        )}

        <Button vType="outline-primary" onClick={resetDate}>
          <FormattedMessage id="TODAY" />
        </Button>

        {!disableViewSwitcher && (
          <ViewTypeSelector
            hideMonthly={hideMonthly}
            selectedView={viewType}
            setViewType={setViewType}
          />
        )}
        <Button
          vType="outline-primary"
          onClick={() => {
            if (refetch.current) refetch.current();
          }}
          className="ml-2 fixed40button"
          size="sm"
        >
          <FontAwesomeIcon icon={faArrowsRotate} />
        </Button>
      </div>
    </div>
  );
};

const CalendarInput = ({ setSelectedDate, selectedDate }) => {
  const location = useLocation();
  const f = useIntl().formatMessage;

  const { year, month } = useMemo(() => {
    const month = selectedDate.getMonth();
    const year = selectedDate.getFullYear();
    return { month, year };
  }, [selectedDate]);

  const isOnAgenda = useMemo(() => {
    return (
      location.pathname.includes("calendar") ||
      location.pathname.includes("timemanager")
    );
  }, [location.pathname]);

  const changeTimeoutRef = useRef(null);
  const selectedDateRef = useRef(selectedDate);

  // keep a ref of the latest selectedDate to avoid stale closures and duplicate updates
  React.useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  // clear any pending timeout on unmount
  React.useEffect(() => {
    return () => {
      if (changeTimeoutRef.current) {
        clearTimeout(changeTimeoutRef.current);
        changeTimeoutRef.current = null;
      }
    };
  }, []);

  const handleDebouncedChange = (value) => {
    // Always clear any existing pending update first so stale timers don't fire
    if (changeTimeoutRef.current) {
      clearTimeout(changeTimeoutRef.current);
      changeTimeoutRef.current = null;
    }

    // If incoming value is invalid, bail out (we already cleared any pending timer above)
    if (!value || isNaN(value.getTime())) return;

    // If the incoming value equals current selectedDate, don't schedule update
    if (
      selectedDateRef.current &&
      value.getTime() === selectedDateRef.current.getTime()
    )
      return;

    // Schedule the trailing update
    changeTimeoutRef.current = setTimeout(() => {
      // Only apply if current selected date still differs (to avoid duplicate sets)
      if (
        !selectedDateRef.current ||
        value.getTime() !== selectedDateRef.current.getTime()
      ) {
        setSelectedDate(value);
      }
      changeTimeoutRef.current = null;
    }, 600);
  };

  if (isOnAgenda)
    return (
      <div className="align-items-center d-flex gap-3">
        <div className="flex-0-0-auto w-175px">
          <ArDatePicker
            value={selectedDate}
            enableHours={false}
            onChange={(value) => {
              handleDebouncedChange(value);
            }}
          />
        </div>
        <span>{`${f({ id: monthTranslation[month] })}, ${year}`}</span>
      </div>
    );

  return <>{`${f({ id: monthTranslation[month] })}, ${year}`}</>;
};

const setCookie = (name, value, days = 365) => {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/`;
};

const getCookieValue = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.split("=")[1] || "");
};

const Calendar = ({ settings, disableViewSwitcher, defaultView }) => {
  const viewAwareStuff = useContext(ServerAwareContext);
  const { params, onParamsChange } = viewAwareStuff || {};
  const history = useHistory();
  const refetchRef = useRef();
  const agendaPreferences = useAgendaPreferences();
  const handleCurrentViewChanges = useContext(AgendaCurrentViewChangesContext);

  const agendaViewCalendarType = useMemo(() => {
    if (agendaPreferences?.calendarView) return agendaPreferences.calendarView;
    else return null;
  }, [agendaPreferences]);

  const [viewType, setViewType] = useState(() => {
    let queryParams = querystring.parse(window.location.search);
    const urlViewType = params
      ? Number(params?.calendarView)
      : Number(queryParams?.calendarView);
    return isValidCalendarView(urlViewType)
      ? urlViewType
      : defaultView || CalendarViewEnum.Month;
  });

  const setFilterUrl = useCallback(
    (params) => {
      const { additionalParams } = params;
      let queryParams = querystring.parse(window.location.search);

      if (additionalParams) {
        for (const param in additionalParams) {
          if (additionalParams.hasOwnProperty(param)) {
            const element = additionalParams[param];
            queryParams[param] = element;
          }
        }
      }

      const newSearch = querystring.stringify(queryParams);
      // post("Preference/Set", {
      //   Key: preferenceKey,
      //   Value: newSearch
      // });
      // saveToLocalStorage(newSearch, schema);

      history.push({
        search: newSearch,
      });
    },
    [history],
  );

  const [selectedDate, setSelectedDate] = useState(() => {
    // const date = new Date(2020, 10, 16);
    let queryParams = querystring.parse(window.location.search);
    const urlDate = params
      ? Number(params?.calendarDate)
      : Number(queryParams?.calendarDate);

    const date = isNaN(urlDate) ? new Date() : new Date(urlDate);

    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  });
  const handleUpdateDate = useCallback(
    (newD) => {
      if (onParamsChange)
        onParamsChange({ additionalParams: { calendarDate: newD.getTime() } });
      else {
        setFilterUrl({ additionalParams: { calendarDate: newD.getTime() } });
      }
      setSelectedDate((oldD) =>
        newD.getTime() !== oldD.getTime() ? newD : oldD,
      );
    },
    [onParamsChange, setFilterUrl],
  );

  const handleResetDate = () => {
    const date = new Date();
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    if (onParamsChange)
      onParamsChange({ additionalParams: { calendarDate: undefined } });
    else setFilterUrl({ additionalParams: { calendarDate: undefined } });
    setSelectedDate(date);
  };

  const currentUser = useCurrentAccount();

  const selectedUser = useMemo(() => {
    if (agendaPreferences?.selectedUser) return agendaPreferences.selectedUser;
    else return currentUser.Id;
  }, [currentUser, agendaPreferences]);

  const setSelectedUser = useCallback(
    (e) => {
      if (typeof handleCurrentViewChanges === "function") {
        handleCurrentViewChanges({
          selectedUser: e,
        });
      }
    },
    [handleCurrentViewChanges],
  );

  // const [selectedUser, setSelectedUser] = useState(currentUser.Id);

  const location = useLocation();

  const isOnAgenda = useMemo(() => {
    return (
      location.pathname.includes("calendar") ||
      location.pathname.includes("timemanager")
    );
  }, [location.pathname]);

  const handleViewTypeUpdate = useCallback(
    (viewType) => {
      if (onParamsChange)
        onParamsChange({
          additionalParams: {
            calendarView: defaultView === viewType ? undefined : viewType,
          },
        });
      else
        setFilterUrl({
          additionalParams: {
            calendarView: defaultView === viewType ? undefined : viewType,
          },
        });
      setViewType(viewType);
      if (isOnAgenda) setCookie(`agenda_calendar_view`, String(viewType));
    },
    [defaultView, isOnAgenda, onParamsChange, setFilterUrl],
  );

  return (
    <div id="calendar-container-one" className="position-relative h-fit flex-1 pb-4 disable-selection d-flex flex-column">
      <AgendaSelectedUserContext.Provider value={selectedUser}>
        <CalenderRefetchRefContext.Provider value={refetchRef}>
          <CalendarUpdateDateContext.Provider value={handleUpdateDate}>
            <CalendarHeader
              viewType={agendaViewCalendarType || viewType}
              hideMonthly={settings?.hideMonthlyView}
              setViewType={handleViewTypeUpdate}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              selectedDate={selectedDate}
              disableViewSwitcher={disableViewSwitcher}
              setSelectedDate={handleUpdateDate}
              resetDate={handleResetDate}
              refetch={refetchRef}
            />
            <FilterColumnColorProvider>
              <CalendarContent
                settings={settings}
                type={agendaViewCalendarType || viewType}
                selectedDate={selectedDate}
                setSelectedDate={handleUpdateDate}
              />
            </FilterColumnColorProvider>
          </CalendarUpdateDateContext.Provider>
        </CalenderRefetchRefContext.Provider>
      </AgendaSelectedUserContext.Provider>
    </div>
  );
};

export default Calendar;
